import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  Layers, Shield, Brain, Cpu, BarChart3, Rocket, Scale,
  FileText, Terminal, Globe, ExternalLink,
  ChevronDown, Copy, Check, Database,
  Zap, Eye, Lock, Code2, Download,
  Box, Activity, GraduationCap, Building2, FlaskConical,
  Mail, Globe2, MapPin, Send
} from 'lucide-react';
import './App.css';

function Section({ children, className = '', id = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section ref={ref} id={id} className={className}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}>
      {children}
    </motion.section>
  );
}

function Particles() {
  return (
    <div className="particles">
      {[...Array(20)].map((_, i) => (
        <motion.div key={i} className="particle"
          initial={{ x: Math.random() * 1200, y: Math.random() * 800, opacity: 0 }}
          animate={{ y: [null, -200, 800], opacity: [0, 0.4, 0] }}
          transition={{ duration: 8 + Math.random() * 12, repeat: Infinity, delay: Math.random() * 5, ease: 'linear' }}
          style={{ width: 3 + Math.random() * 4, height: 3 + Math.random() * 4 }} />
      ))}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return <button className="copy-btn" onClick={handleCopy}>{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Copied' : 'Copy'}</button>;
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { const h = () => setScrolled(window.scrollY > 50); window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h); }, []);
  const links = [
    { label: 'Architecture', href: '#architecture' },
    { label: 'Layers', href: '#layers' },
    { label: 'Tools', href: '#tools' },
    { label: 'Models', href: '#models' },
    { label: 'Research', href: '#research' },
    { label: 'Contact', href: '#contact' },
  ];
  return (
    <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
      <div className="nav__inner">
        <a href="#hero" className="nav__logo"><img src="/logo.png" alt="AISA" className="nav__logo-img" /></a>
        <div className={`nav__links ${menuOpen ? 'nav__links--open' : ''}`}>
          {links.map(l => <a key={l.href} href={l.href} className="nav__link" onClick={() => setMenuOpen(false)}>{l.label}</a>)}
          <a href="https://pypi.org/project/aisa-checker/" className="nav__cta" target="_blank" rel="noreferrer">Get Started</a>
        </div>
        <button className="nav__hamburger" onClick={() => setMenuOpen(!menuOpen)}><span /><span /><span /></button>
      </div>
    </nav>
  );
}

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  return (
    <section id="hero" className="hero">
      <Particles />
      <motion.div className="hero__content" style={{ y, opacity }}>
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
          <img src="/logo.png" alt="AISA" className="hero__logo" />
        </motion.div>
        <motion.h1 className="hero__title" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}>
          A Unified Architecture for <span className="gradient-text">Agentic AI Systems</span>
        </motion.h1>
        <motion.p className="hero__subtitle" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }}>
          The first layered reference architecture for designing, deploying, evaluating, and governing autonomous AI agents. 7 layers. 4 contracts. One unified framework.
        </motion.p>
        <motion.div className="hero__actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}>
          <a href="#tools" className="btn btn--primary"><Terminal size={18} /> Get Started</a>
          <a href="/aisa-paper.pdf" className="btn btn--secondary" target="_blank" rel="noreferrer"><FileText size={18} /> Read Paper</a>
          <a href="https://huggingface.co/AISA-Framework" className="btn btn--ghost" target="_blank" rel="noreferrer"><Database size={18} /> HuggingFace</a>
        </motion.div>
        <motion.div className="hero__install" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.7 }}>
          <code>pip install aisa-checker</code>
          <CopyButton text="pip install aisa-checker" />
        </motion.div>
        <motion.div className="hero__scroll" animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}><ChevronDown size={24} /></motion.div>
      </motion.div>
    </section>
  );
}

function Stats() {
  const stats = [
    { value: '7', label: 'Architectural Layers' },
    { value: '4', label: 'Cross-Layer Contracts' },
    { value: '55', label: 'Evaluation Criteria' },
    { value: '8', label: 'Frameworks Mapped' },
  ];
  return (
    <Section className="stats">
      <div className="stats__grid">
        {stats.map((s, i) => (
          <motion.div key={i} className="stat" initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}>
            <span className="stat__value">{s.value}</span>
            <span className="stat__label">{s.label}</span>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Problem() {
  return (
    <Section className="problem">
      <div className="container">
        <h2 className="section-title">The Problem</h2>
        <p className="section-subtitle">Agentic AI frameworks are architecturally incomplete</p>
        <div className="problem__grid">
          {[
            { icon: <Eye />, title: 'No Evaluation', desc: 'Only 29% average coverage across 8 major frameworks. No regression testing, no behavioral monitoring.', stat: '29%' },
            { icon: <Scale />, title: 'No Governance', desc: 'Policy-as-code, fairness auditing, and compliance are absent from every framework.', stat: '17%' },
            { icon: <Rocket />, title: 'No Lifecycle', desc: 'No CI/CD for agents, no A/B testing, no versioned deployments, no staged rollout.', stat: '25%' },
            { icon: <Lock />, title: 'No Budget Control', desc: 'Token budgets and cost ceilings score near-zero. Agents can spiral without limits.', stat: '4%' },
          ].map((item, i) => (
            <motion.div key={i} className="problem__card" whileHover={{ y: -8, boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
              <div className="problem__icon">{item.icon}</div>
              <div className="problem__stat">{item.stat}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>
        <p className="problem__source">Based on our mapping study of LangChain, CrewAI, AutoGen, OpenAI, Claude SDK, LlamaIndex, Semantic Kernel, and Google ADK.</p>
      </div>
    </Section>
  );
}

function Architecture() {
  return (
    <Section className="architecture" id="architecture">
      <div className="container">
        <h2 className="section-title">The Architecture</h2>
        <p className="section-subtitle">7 layers spanning from LLM foundations to governance, connected by 4 cross-layer contracts</p>
        <motion.div className="architecture__diagram" whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
          <img src="/architecture.png" alt="AISA Architecture" />
        </motion.div>
      </div>
    </Section>
  );
}

const LAYERS = [
  { id: 'L1', name: 'LLM Foundation', color: '#4a7ab5', icon: <Cpu />, desc: 'Core intelligence \u2014 language understanding, reasoning, inference', keys: ['Tokenization & Inference', 'Prompt Engineering', 'Instruction Tuning', 'Context Window Optimization', 'Safety Filters & Grounding'] },
  { id: 'L2', name: 'Tool & Environment', color: '#5a8ec4', icon: <Code2 />, desc: 'Interface for external actions \u2014 how the agent "does things"', keys: ['Tool Creation & Syntax', 'Code Execution & Sandboxing', 'Multi-Call Protocol (MCP)', 'Permission & Rate Control', 'Structured I/O with APIs'] },
  { id: 'L3', name: 'Cognitive Agent', color: '#7ba3d4', icon: <Brain />, desc: 'Agent reasoning, reflection, and self-improvement', keys: ['Task Planning & Decomposition', 'Reflection Loops', 'Memory Management', 'Multi-turn Reasoning', 'Goal Tracking'] },
  { id: 'L4', name: 'Agentic Infrastructure', color: '#9bb5de', icon: <Box />, desc: 'Coordination, orchestration, and scalability backbone', keys: ['Workflow Orchestration', 'State Coordination', 'Multi-Agent RAG Systems', 'Logging & Monitoring', 'Latency & Cost Optimization'] },
  { id: 'L5', name: 'Evaluation & Feedback', color: '#c4a8c8', icon: <BarChart3 />, desc: 'Continuous improvement and validation framework', keys: ['Behavioral Monitoring', 'Human-in-the-Loop Evals', 'Automated Regression Tests', 'Error Analysis & Prioritization', 'Quality Metrics'] },
  { id: 'L6', name: 'Development & Deployment', color: '#d4b0c0', icon: <Rocket />, desc: 'Operational lifecycle for agentic systems', keys: ['Version Control of Agents', 'CI/CD & Benchmarking', 'A/B Testing of Workflows', 'Security & Access Control', 'Model Lifecycle Tracking'] },
  { id: 'L7', name: 'Governance, Ethics & Policy', color: '#deb8c8', icon: <Shield />, desc: 'Top layer ensuring alignment with human and institutional values', keys: ['AI Policies & Transparency', 'Fairness & Bias Mitigation', 'Privacy & Data Protection', 'Regulatory Compliance', 'Ethical Reflection & Explainability'] },
];

function LayersSection() {
  const [active, setActive] = useState(0);
  return (
    <Section className="layers" id="layers">
      <div className="container">
        <h2 className="section-title">Seven Layers of Assurance</h2>
        <p className="section-subtitle">Each layer has explicit responsibilities, failure modes, controls, and metrics</p>
        <div className="layers__content">
          <div className="layers__tabs">
            {LAYERS.map((layer, i) => (
              <motion.button key={i} className={`layers__tab ${i === active ? 'layers__tab--active' : ''}`} onClick={() => setActive(i)} whileHover={{ x: 4 }}
                style={i === active ? { borderLeftColor: layer.color, background: `${layer.color}10` } : {}}>
                <span className="layers__tab-icon" style={{ color: layer.color }}>{layer.icon}</span>
                <span className="layers__tab-id">{layer.id}</span>
                <span className="layers__tab-name">{layer.name}</span>
              </motion.button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={active} className="layers__detail" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <div className="layers__detail-header" style={{ color: LAYERS[active].color }}>
                {LAYERS[active].icon}
                <h3>{LAYERS[active].id}: {LAYERS[active].name}</h3>
              </div>
              <p className="layers__detail-desc">{LAYERS[active].desc}</p>
              <div className="layers__detail-keys">
                {LAYERS[active].keys.map((key, i) => (
                  <motion.div key={i} className="layers__key" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} style={{ borderLeftColor: LAYERS[active].color }}>
                    <Zap size={14} style={{ color: LAYERS[active].color }} /> {key}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Section>
  );
}

function Contracts() {
  const contracts = [
    { name: 'Policy', icon: <Shield size={28} />, desc: 'Enforce governance and safety constraints across all layers', color: '#e74c3c' },
    { name: 'Telemetry', icon: <Activity size={28} />, desc: 'Enable auditability and reproducibility with end-to-end tracing', color: '#3498db' },
    { name: 'Versioning', icon: <Layers size={28} />, desc: 'Ensure reproducibility and controlled evolution of agents', color: '#2ecc71' },
    { name: 'Budget', icon: <BarChart3 size={28} />, desc: 'Control cost, latency, and resource consumption at runtime', color: '#f39c12' },
  ];
  return (
    <Section className="contracts">
      <div className="container">
        <h2 className="section-title">Cross-Layer Contracts</h2>
        <p className="section-subtitle">Four contracts that bind all layers together with enforceable guarantees</p>
        <div className="contracts__grid">
          {contracts.map((c, i) => (
            <motion.div key={i} className="contracts__card" whileHover={{ y: -6, scale: 1.02 }} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <div className="contracts__icon" style={{ color: c.color, background: `${c.color}15` }}>{c.icon}</div>
              <h3>{c.name} Contract</h3>
              <p>{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function Tools() {
  return (
    <Section className="tools" id="tools">
      <div className="container">
        <h2 className="section-title">Tools & Ecosystem</h2>
        <p className="section-subtitle">Everything you need to evaluate and build AISA-compliant systems</p>
        <div className="tools__grid">
          <motion.div className="tools__card tools__card--featured" whileHover={{ y: -6 }}>
            <div className="tools__card-badge">CLI Tool</div>
            <Terminal size={32} className="tools__card-icon" />
            <h3>aisa-checker</h3>
            <p>Evaluate any agentic AI project against all 7 AISA layers and 4 contracts.</p>
            <div className="tools__install"><code>pip install aisa-checker</code><CopyButton text="pip install aisa-checker" /></div>
            <div className="tools__commands">
              <div className="tools__cmd"><code>aisa check ./project</code><span>Pattern scan (free)</span></div>
              <div className="tools__cmd"><code>aisa ai-check ./project</code><span>AI-powered analysis</span></div>
              <div className="tools__cmd"><code>aisa full-check ./project</code><span>Combined (best)</span></div>
              <div className="tools__cmd"><code>aisa assess</code><span>Questionnaire</span></div>
            </div>
            <a href="https://pypi.org/project/aisa-checker/" className="btn btn--primary btn--sm" target="_blank" rel="noreferrer"><Download size={16} /> PyPI</a>
          </motion.div>
          <motion.div className="tools__card" whileHover={{ y: -6 }}>
            <Globe size={32} className="tools__card-icon" />
            <h3>Web App</h3>
            <p>Upload your project ZIP and get an instant AISA compliance report online.</p>
            <a href="https://huggingface.co/spaces/AISA-Framework/aisa-checker" className="btn btn--secondary btn--sm" target="_blank" rel="noreferrer"><ExternalLink size={16} /> Open App</a>
          </motion.div>
          <motion.div className="tools__card" whileHover={{ y: -6 }}>
            <FlaskConical size={32} className="tools__card-icon" />
            <h3>Colab Notebook</h3>
            <p>Interactive notebook to run AISA checks. Upload, scan, view reports.</p>
            <a href="https://colab.research.google.com/drive/1mkKVK2mW33dx2OuTofAIGBp6DxyY5gSo?usp=sharing" className="btn btn--secondary btn--sm" target="_blank" rel="noreferrer"><ExternalLink size={16} /> Open in Colab</a>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

function ModelsDatasets() {
  return (
    <Section className="models" id="models">
      <div className="container">
        <h2 className="section-title">Arabic Models & Datasets</h2>
        <p className="section-subtitle">
          Built with AISA — production-ready Arabic function-calling models and datasets for agentic AI
        </p>

        <div className="models__showcase">
          {/* Dataset Card */}
          <motion.div className="models__card" whileHover={{ y: -6 }}>
            <div className="models__card-badge models__card-badge--data">Dataset</div>
            <div className="models__card-header">
              <Database size={28} className="models__card-icon" />
              <div>
                <h3>AISA-AR-FunctionCall</h3>
                <span className="models__card-tag">Apache 2.0</span>
              </div>
            </div>
            <p className="models__card-desc">Large-scale Arabic structured function-calling dataset. 50K+ samples across 5 dialects, 8 domains, and 27 tool schemas.</p>
            <div className="models__stats">
              <div className="models__stat-item"><strong>50,810</strong><span>Samples</span></div>
              <div className="models__stat-item"><strong>5</strong><span>Dialects</span></div>
              <div className="models__stat-item"><strong>8</strong><span>Domains</span></div>
              <div className="models__stat-item"><strong>27</strong><span>Tools</span></div>
            </div>
            <div className="models__dialects">
              {['MSA', 'Gulf', 'Egyptian', 'Levantine', 'Maghrebi'].map(d => (
                <span key={d} className="models__dialect-tag">{d}</span>
              ))}
            </div>
            <div className="models__domains">
              {['Travel', 'Healthcare', 'Banking', 'E-commerce', 'Weather', 'Gov Services', 'Utilities', 'Islamic'].map(d => (
                <span key={d} className="models__domain-tag">{d}</span>
              ))}
            </div>
            <a href="https://huggingface.co/datasets/AISA-Framework/AISA-AR-FunctionCall" className="btn btn--secondary btn--sm" target="_blank" rel="noreferrer"><ExternalLink size={16} /> View on HuggingFace</a>
          </motion.div>

          {/* Model Card - FT */}
          <motion.div className="models__card" whileHover={{ y: -6 }}>
            <div className="models__card-badge models__card-badge--model">Model</div>
            <div className="models__card-header">
              <Cpu size={28} className="models__card-icon" />
              <div>
                <h3>AISA-AR-FunctionCall-FT</h3>
                <span className="models__card-tag">Gemma 3 270M</span>
              </div>
            </div>
            <p className="models__card-desc">Fully fine-tuned Arabic function-calling model. Converts natural Arabic requests into structured executable API calls.</p>
            <div className="models__results">
              <h4>Key Results</h4>
              <div className="models__result-grid">
                <div className="models__result">
                  <span className="models__result-value models__result-value--good">65.5%</span>
                  <span className="models__result-label">Function Accuracy</span>
                </div>
                <div className="models__result">
                  <span className="models__result-value models__result-value--good">99.2%</span>
                  <span className="models__result-label">Format Validity</span>
                </div>
                <div className="models__result">
                  <span className="models__result-value models__result-value--great">&lt;1%</span>
                  <span className="models__result-label">Parse Failure</span>
                </div>
                <div className="models__result">
                  <span className="models__result-value models__result-value--good">33.6%</span>
                  <span className="models__result-label">Full Match</span>
                </div>
              </div>
              <p className="models__result-note">Parse failure reduced from 87% to &lt;1% vs baseline</p>
            </div>
            <a href="https://huggingface.co/AISA-Framework/AISA-AR-FunctionCall-FT" className="btn btn--secondary btn--sm" target="_blank" rel="noreferrer"><ExternalLink size={16} /> View on HuggingFace</a>
          </motion.div>

          {/* Model Card - Think */}
          <motion.div className="models__card" whileHover={{ y: -6 }}>
            <div className="models__card-badge models__card-badge--model">Model</div>
            <div className="models__card-header">
              <Brain size={28} className="models__card-icon" />
              <div>
                <h3>AISA-AR-FunctionCall-Think</h3>
                <span className="models__card-tag">Reasoning-Augmented</span>
              </div>
            </div>
            <p className="models__card-desc">Reasoning-augmented variant that adds chain-of-thought before tool invocation for improved accuracy on complex queries.</p>
            <div className="models__example">
              <h4>Example</h4>
              <div className="models__example-block">
                <div className="models__example-input">
                  <span className="models__example-label">Input (Arabic)</span>
                  <code dir="rtl">ما حالة الطقس في الرياض اليوم؟</code>
                </div>
                <div className="models__example-output">
                  <span className="models__example-label">Output</span>
                  <code>call:get_weather&#123;city:الرياض, days:1&#125;</code>
                </div>
              </div>
            </div>
            <a href="https://huggingface.co/AISA-Framework/AISA-AR-FunctionCall-Think" className="btn btn--secondary btn--sm" target="_blank" rel="noreferrer"><ExternalLink size={16} /> View on HuggingFace</a>
          </motion.div>
        </div>

        <div className="models__collection">
          <a href="https://huggingface.co/collections/AISA-Framework/aisa-arabic-functioncall-datasets-and-models" className="btn btn--primary" target="_blank" rel="noreferrer">
            <Database size={18} /> View Full Collection on HuggingFace
          </a>
        </div>
      </div>
    </Section>
  );
}

function Research() {
  const [showPaper, setShowPaper] = useState(false);
  return (
    <Section className="research" id="research">
      <div className="container">
        <h2 className="section-title">Research</h2>
        <p className="section-subtitle">The academic foundation behind AISA</p>
        <div className="research__content">
          <div className="research__main">
            <motion.div className="research__paper-embed" whileHover={{ y: -4, boxShadow: '0 16px 48px rgba(0,0,0,0.15)' }} onClick={() => setShowPaper(true)}>
              <iframe src="/aisa-paper.pdf#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH" title="AISA Paper Preview" className="research__pdf-inline" />
              <div className="research__pdf-click-overlay">
                <Eye size={20} />
                <span>Click to expand</span>
              </div>
            </motion.div>
            <div className="research__paper-info">
              <h3>AISA: A Unified Architecture for Agentic AI Systems</h3>
              <p className="research__created">Created by <a href="https://www.linkedin.com/in/omarnj/?skipRedirect=true" target="_blank" rel="noreferrer" className="research__author-link">Omer Nacar</a></p>
              <p className="research__venue">Tuwaiq Academy &bull; January 2026</p>
              <p className="research__abstract">Recent advances in large language models have enabled the emergence of agentic AI systems capable of autonomous reasoning, planning, tool use, and multi-step interaction with complex environments. However, current agentic systems are often developed in an ad hoc and fragmented manner, lacking a unified architectural framework that integrates reasoning, infrastructure, evaluation, governance, and ethical oversight. This paper introduces AISA (Agentic AI Systems Architecture), a layered conceptual framework that provides a holistic reference architecture for designing, deploying, and governing agentic AI systems.</p>
              <div className="research__actions">
                <a href="/aisa-paper.pdf" className="btn btn--primary btn--sm" target="_blank" rel="noreferrer"><Download size={16} /> Download PDF</a>
              </div>
            </div>
          </div>
          <div className="research__citation">
            <h3>Citation</h3>
            <div className="code-block">
              <CopyButton text={`@misc{nacar2026aisa,\n  title={AISA: A Unified Architecture for Agentic AI Systems},\n  author={Nacar, O. and Deema, A. and Mohammed, A.},\n  year={2026},\n  publisher={Zenodo},\n  doi={10.5281/zenodo.18161880},\n  url={https://doi.org/10.5281/zenodo.18161880},\n}`} />
              <pre>{`@misc{nacar2026aisa,
  title={AISA: A Unified Architecture for Agentic AI Systems},
  author={Nacar, O. and Deema, A. and Mohammed, A.},
  year={2026},
  publisher={Zenodo},
  doi={10.5281/zenodo.18161880},
  url={https://doi.org/10.5281/zenodo.18161880},
}`}</pre>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showPaper && (
          <motion.div className="paper-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowPaper(false)}>
            <motion.div className="paper-modal__content" initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={(e) => e.stopPropagation()}>
              <button className="paper-modal__close" onClick={() => setShowPaper(false)}>&times;</button>
              <iframe src="/aisa-paper.pdf" title="AISA Paper" className="paper-modal__iframe" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
}

function Roadmap() {
  const phases = [
    { icon: <GraduationCap />, title: 'Learning', desc: 'Basic model invocation, simple tools, manual testing', status: 'Foundation' },
    { icon: <FlaskConical />, title: 'Prototyping', desc: 'Single-agent planning, basic orchestration, offline evals', status: 'Development' },
    { icon: <Building2 />, title: 'Enterprise', desc: 'Multi-agent coordination, CI/CD, distributed tracing', status: 'Production' },
    { icon: <Shield />, title: 'Governed', desc: 'Policy-as-code, compliance, human oversight, full contracts', status: 'Mature' },
  ];
  return (
    <Section className="roadmap">
      <div className="container">
        <h2 className="section-title">Adoption Roadmap</h2>
        <p className="section-subtitle">AISA scales from learning to enterprise-grade governed systems</p>
        <div className="roadmap__track">
          {phases.map((p, i) => (
            <motion.div key={i} className="roadmap__phase" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}>
              <div className="roadmap__dot" />
              <div className="roadmap__card">
                <div className="roadmap__icon">{p.icon}</div>
                <span className="roadmap__status">{p.status}</span>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}


function Contact() {
  const [status, setStatus] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const response = await fetch('https://formspree.io/f/xleyozke', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus(''), 4000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus(''), 4000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus(''), 4000);
    }
  };

  return (
    <Section className="contact" id="contact">
      <div className="container">
        <h2 className="section-title">Get in Touch</h2>
        <p className="section-subtitle">Have questions about AISA? Want to collaborate? We'd love to hear from you.</p>

        <div className="contact__content">
          <div className="contact__info">
            <div className="contact__location">
              <MapPin size={20} />
              <div>
                <strong>Based in</strong>
                <p>Riyadh, Saudi Arabia</p>
              </div>
            </div>

            <div className="contact__links">
              <a href="mailto:eng.omarnj@gmail.com" className="contact__link">
                <div className="contact__link-icon"><Mail size={20} /></div>
                <div>
                  <strong>Email</strong>
                  <span>eng.omarnj@gmail.com</span>
                </div>
              </a>
              <a href="https://www.linkedin.com/in/omarnj/?skipRedirect=true" target="_blank" rel="noreferrer" className="contact__link">
                <div className="contact__link-icon contact__link-icon--linkedin"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg></div>
                <div>
                  <strong>LinkedIn</strong>
                  <span>Omer Nacar</span>
                </div>
              </a>
              <a href="https://omarai.me" target="_blank" rel="noreferrer" className="contact__link">
                <div className="contact__link-icon contact__link-icon--web"><Globe2 size={20} /></div>
                <div>
                  <strong>Website</strong>
                  <span>omarai.me</span>
                </div>
              </a>
            </div>
          </div>

          <motion.form className="contact__form" onSubmit={handleSubmit} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="contact__field">
              <label htmlFor="name">Name</label>
              <input id="name" type="text" required placeholder="Your name" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div className="contact__field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" required placeholder="your@email.com" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div className="contact__field">
              <label htmlFor="message">Message</label>
              <textarea id="message" required rows={5} placeholder="Tell us about your project or question..." value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })} />
            </div>
            <button type="submit" className="btn btn--primary contact__submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending...' : <><Send size={16} /> Send Message</>}
            </button>
            {status === 'success' && <p className="contact__toast contact__toast--success">Message sent successfully!</p>}
            {status === 'error' && <p className="contact__toast contact__toast--error">Failed to send. Please try again.</p>}
          </motion.form>
        </div>
      </div>
    </Section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__content">
          <div className="footer__brand"><img src="/logo.png" alt="AISA" className="footer__logo" /><p>Agentic AI Systems Architecture</p><p className="footer__org">Tuwaiq Academy - TRDC</p></div>
          <div className="footer__links"><h4>Resources</h4><a href="/aisa-paper.pdf" target="_blank" rel="noreferrer">Paper</a><a href="https://pypi.org/project/aisa-checker/" target="_blank" rel="noreferrer">PyPI</a><a href="https://huggingface.co/AISA-Framework" target="_blank" rel="noreferrer">HuggingFace</a><a href="https://huggingface.co/spaces/AISA-Framework/aisa-checker" target="_blank" rel="noreferrer">Web App</a></div>
          <div className="footer__links"><h4>Architecture</h4><a href="#layers">7 Layers</a><a href="#architecture">Overview</a><a href="#tools">Tools</a><a href="#research">Research</a><a href="#contact">Contact</a></div>
        </div>
        <div className="footer__bottom"><p>&copy; 2026 AISA Project. MIT License.</p></div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="app">
      <Nav />
      <Hero />
      <Stats />
      <Problem />
      <Architecture />
      <LayersSection />
      <Contracts />
      <Tools />
      <ModelsDatasets />
      <Research />
      <Roadmap />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
